import { Component, ElementRef, ViewChild, Input, Output, EventEmitter, HostListener, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatStreamFn, parseChatJson, streamChatOnce } from '../../../utils-lib/chat-stream';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import {TaxPlanningService} from "../../../tpiq-services/tax-planning.service";
import { KnolliChatService } from '../../../../app/tpiq-services/knolli-chat.service';
import { ChatMessage } from './rap-questionnaire';
import {Session} from "../../../services/Session";
import {LoadingService} from "../../../services/LoadingService";
import { environment } from "../../../../environments/environment";
import { MarkdownModule } from 'ngx-markdown';
import { trackPendoEvent } from '../../../utils-lib/pendo-track';
import {MatExpansionModule} from "@angular/material/expansion";
import { NumeralService } from '../../../services/Numeral.service';
import { PROVINCES } from '../../../constants/Provinces.constants';
import { buildDiscoveryWorkingSet } from '../../../utils-lib/discovery-working-set';


declare const _: any;
@Component({
  selector: 'app-rap-questionnaire',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MarkdownModule,
    MatExpansionModule
  ],
  templateUrl: './rap-questionnaire.component.html',
  styleUrls: ['./rap-questionnaire.component.scss']
})
export class RapQuestionnaireComponent implements OnChanges {

  /**
   * Deadline for a chat stream, in milliseconds.
   *
   * The prod chat load balancer cuts an idle stream at 60s, and the slowest
   * answer measured over a week was 43s. 90s therefore sits above both, so
   * this timer only fires when the stream stalls with no error at all.
   */
  static readonly CHAT_TIMEOUT_MS = 90000;

  /**
   * Test seam for the chat stream. Specs replace it with a fake, so they can
   * drive the close, error, and timeout paths with no network. Leave it unset
   * in the app, and streamChatOnce uses the real fetch-event-source.
   */
  streamFn?: ChatStreamFn;

  @Input() ocrData: any;
  @Input() plan: any;
  @Input() chatId : any;
  @Input() documentId : any;
  @Input() questionnaireTrack : any;
  @Input() aiSuggestedStrategies: any;
  @Input() questionnaireUtilization: any;
  /** 
   * Flag to indicate if this is a RAP edit session
   * When true, chatId should be stored in RAP session payload, not database
   */
  @Input() isRapEditMode: boolean = false;
  /** 
   * Session ID for RAP mode (used for session-scoped localStorage key)
   * Prevents cross-tab leakage when same plan is edited in multiple tabs
   */
  @Input() sessionId?: string;
  org: string =  Session.getOrg();
  user: string = Session.getUser().id;
  userDetails = Session.getUser();
  copiedIndex: number | null = null;
  streamCompletedIndexes = new Set<number>();
  // Messages come from parent component
  @Input() messages: ChatMessage[] = [];
  @Output() messagesChange = new EventEmitter<ChatMessage[]>();
  @Output() nextClicked = new EventEmitter<string>();
  @Output() sectionChange = new EventEmitter<number>();
  /** 
   * Emits chatId changes to parent component
   * Parent stores chatId in RAP session payload for cross-device persistence
   */
  @Output() chatIdChange = new EventEmitter<number>();
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('chatInput') chatInput!: ElementRef;

  userInput = '';
  isStreaming = false;
  isLoading = false;
  aiAssistantId = environment.questionnaireAiAssistantId;
  aiResponse = '';
  chatApiUrl = environment.KNOLLI_CHAT_URL + "/api/public/ai-chat";
  private downX = 0;
  private downY = 0;
  engagementTracked = false;
  

  /** Injects the services for plan data, the Knolli chat, loading state and amount formatting. */
  constructor(private taxPlanningService: TaxPlanningService, private knolliChatService: KnolliChatService, private loadingService: LoadingService, private numeralService: NumeralService) { }

  async ngOnInit() {
    if(this.questionnaireTrack == 1){
      if (!this.messages || this.messages.length === 0) {
        await this.getWelcomeMessage();
        if(this.chatId){
          this.loadOldMessages(this.chatId);
        }
        }
      }
  }

  ngAfterViewInit() {
    this.focusChatInput();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['aiSuggestedStrategies']) {
      this.aiSuggestedStrategies = _.orderBy(
        this.aiSuggestedStrategies,
        [(item) => Number(item.totalSavings.replace(/[$,]/g, ''))],
        ['desc']
      );
    }
  }
  
 userObj() {
    return {
      firstName: this.userDetails.first_name,
      lastName: this.userDetails.last_name,
      userEmail: this.userDetails.id,
      organisation: this.org
    };
  }

  getUserInitials(): string {
    const firstName = this.userDetails?.first_name?.trim() || '';
    const lastName = this.userDetails?.last_name?.trim() || '';

    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName.charAt(0).toUpperCase();

    return `${firstInitial}${lastInitial}`;
  }

  @HostListener('document:mousedown', ['$event'])
onMouseDown(event: MouseEvent) {
  const target = event.target as HTMLElement;

  if (
    this.chatInput?.nativeElement.contains(target) ||
    target.closest('.input-area') ||
    target.tagName === 'INPUT' ||       // ← add this
    target.tagName === 'TEXTAREA'       // ← and this
  ) {
    return;
  }

  this.downX = event.clientX;
  this.downY = event.clientY;
}

@HostListener('document:mouseup', ['$event'])
onMouseUp(event: MouseEvent) {
  const target = event.target as HTMLElement;

  if (
    this.chatInput?.nativeElement.contains(target) ||
    target.closest('.input-area') ||
    target.tagName === 'INPUT' ||       // ← add this
    target.tagName === 'TEXTAREA'       // ← and this
  ) {
    return;
  }

  const dx = Math.abs(event.clientX - this.downX);
  const dy = Math.abs(event.clientY - this.downY);

  if (dx < 3 && dy < 3) {
    window.getSelection()?.removeAllRanges();
  }
}
  
  initializeChatId() {
    if (!this.plan || !this.plan.id) {
      console.error("Plan ID is missing.");
      return;
    }

    // RAP MODE: Use session-scoped localStorage key and check RAP session payload
    if (this.isRapEditMode) {
      // 1. Check session-scoped localStorage (prevents cross-tab leakage)
      const sessionStorageKey = this.sessionId 
        ? `chatId_${this.plan.id}_${this.sessionId}` 
        : `chatId_${this.plan.id}`; 
      const savedChatKey = localStorage.getItem(sessionStorageKey);
      
      if (savedChatKey) {
        const parts = savedChatKey.split("_");
        if (parts.length >= 2) {
          this.chatId = Number(parts[parts.length - 1]); 
          return;
        }
      }
      
      // 2. If chatId is provided from parent (from RAP session payload), use it
      if (this.chatId) {
        // Store in session-scoped localStorage for immediate access
        const combinedKey = `${this.plan.id}_${this.chatId}`;
        localStorage.setItem(sessionStorageKey, combinedKey);
        return;
      }
      
      // 3. Create new chat session (lazy initialization - only when user sends first message)
      // Note: chatId will be stored in RAP session payload, not database
      this.createNewChatSessionForRap(sessionStorageKey);
      return;
    }

    // NORMAL MODE: Use plan-scoped localStorage and save to database
    const storageKey = `chatId_${this.plan.id}`;
    const savedChatKey = localStorage.getItem(storageKey);

    // Already saved chat for this plan
    if (savedChatKey) {
      const parts = savedChatKey.split("_");
      if (parts.length === 2) {
        this.chatId = Number(parts[1]);
      }
      return;
    }
    
    this.loadingService.triggerLoadingEvent(true);
    // New chat
    
    this.knolliChatService.getChatConfig(this.aiAssistantId, this.userObj()).subscribe({
      next: (res) => {
        this.loadingService.triggerLoadingEvent(false);
        if (res?.id) {
          this.chatId = res.id;
          let data = {
              fileId: this.plan.documentId? this.plan.documentId : this.documentId,
              chatId: this.chatId
            };
          // NORMAL MODE: Save to database (not in RAP mode)
          this.taxPlanningService.saveDRaftPlanChatId(this.org, this.user, data)
                .subscribe({
                  next: () => {},
                  error: (error) => {
                    this.loadingService.triggerLoadingEvent(false);
                  }
           });
          const combinedKey = `${this.plan.id}_${this.chatId}`;
          localStorage.setItem(storageKey, combinedKey);

          this.triggerBackgroundOCRChat(this.buildDiscoveryContext());
        }
      },
      error: (err) => {
        console.error('Chat Config Error:', err)
        this.loadingService.triggerLoadingEvent(false);
      }
    });
  }

  /** State name of the plan home state. Uses the same country list as the wizard. */
  private homeStateName(): string {
    const country = this.userDetails?.orgCountryCode || 'US';
    const found = PROVINCES.COUNTRY_PROVINCES.find((state: any) =>
      state.country === country && state.short === this.plan?.homeState);
    return found ? found.name : '';
  }

  /** First prompt of the chat: the OCR data and all RAP strategies. */
  private buildDiscoveryContext(): any {
    return buildDiscoveryWorkingSet({
      ocrData: this.ocrData,
      planningYear: this.plan?.taxYear,
      strategies: this.aiSuggestedStrategies,
      stateName: this.homeStateName(),
      formatAmount: (amount: number) => this.numeralService.format('$0,0', amount)
    });
  }

  /**
   * Creates a new chat session for RAP mode
   * Does NOT save to database (maintains sandbox isolation)
   * Stores chatId in session-scoped localStorage and emits to parent for RAP session storage
   */
  private createNewChatSessionForRap(sessionStorageKey: string): void {
    this.loadingService.triggerLoadingEvent(true);
    this.knolliChatService.getChatConfig(this.aiAssistantId, this.userObj()).subscribe({
      next: (res) => {
        this.loadingService.triggerLoadingEvent(false);
        if (res?.id) {
          this.chatId = res.id;
          
          // RAP MODE: Store in session-scoped localStorage only
          // Do NOT save to database (maintains sandbox isolation)
          const combinedKey = `${this.plan.id}_${this.chatId}`;
          localStorage.setItem(sessionStorageKey, combinedKey);
          
          // Emit chatId to parent component so it can be stored in RAP session payload
          // Parent component will handle storing in rapSession.payload.chatId
          // This ensures cross-device persistence via rap_edit_drafts.json_payload
          this.chatIdChange.emit(this.chatId);
          
          this.triggerBackgroundOCRChat(this.buildDiscoveryContext());
        }
      },
      error: (err) => {
        console.error('[RAP] Chat Config Error:', err);
        this.loadingService.triggerLoadingEvent(false);
      }
    });
  }

  getWelcomeMessage(): Promise<void> {
  return new Promise((resolve) => {
    this.loadingService.triggerLoadingEvent(true);
    this.knolliChatService.getWelcomeMessage(this.aiAssistantId).subscribe({
      next: (response) => {
        if (response?.description) {
          this.messages.push({ sender: 'ai', text: response.description });
        } else {
          this.messages.push({ sender: 'ai', text: '' });
        }

        this.messagesChange.emit(this.messages);
        resolve(); 
      },
      error: () => resolve(),
      complete: () => this.loadingService.triggerLoadingEvent(false)
    });
  });
}

  async sendPrompt() {

    this.isLoading = true;

    if (!this.chatId) {
      console.error("Chat ID not ready yet");
      return;
    }

    const prompt = this.userInput.trim();
    if (!prompt || this.isStreaming) return;

    // Add user message
    this.messages.push({ sender: 'user', text: prompt, showCopy: false, copied: false });
    this.messagesChange.emit(this.messages);
    trackPendoEvent('Discovery Chat Message Sent', { message_index: this.messages.length });
    this.scrollToBottom();

    // Add placeholder AI message
    this.messages.push({ sender: 'ai', text: '' });
    const aiMsgIndex = this.messages.length - 1; // ✅ TRACK INDEX
    this.messagesChange.emit(this.messages);

    /** Track Discovery utilization */

    if (this.messages.length >= 4 && !this.engagementTracked && !this.questionnaireUtilization) {
        this.engagementTracked = true;
        const payload = { documentId: this.documentId, utilization: true};
        this.taxPlanningService.trackQuestionnaireUtilization(this.org,this.user, payload).subscribe();
    }

    this.userInput = '';
    this.isStreaming = true;

    const body = {
      aiAssistantId: this.aiAssistantId,
      chatId: this.chatId,
      prompt: prompt
    };

    const result = await streamChatOnce({
      url: this.chatApiUrl,
      body,
      timeoutMs: RapQuestionnaireComponent.CHAT_TIMEOUT_MS,
      streamFn: this.streamFn,
      onData: (data) => {
        this.isLoading = false;
        this.aiResponse = data;
        // Update last AI message
        this.messages[aiMsgIndex].text = data;
        this.messagesChange.emit(this.messages);
      }
    });

    // streamChatOnce always settles, so these two lines always run. Leaving
    // isStreaming on locks the send button for the rest of the session.
    this.isStreaming = false;
    this.isLoading = false;

    if (result.outcome === 'closed') {
      this.messages[aiMsgIndex].showCopy = true;
      // Fetch the message ID for this response so it can be rated
      this.knolliChatService.getLatestMessageId(this.chatId).subscribe({
        next: (res) => {
          // API returns the latest message id — adjust field name if needed
          const msgId = res?.data?.messageId;
          if (msgId) {
            this.messages[aiMsgIndex].messageId = msgId;
            this.messagesChange.emit(this.messages);
          }
        },
        error: () => {} // non-critical, rating just won't show
      });
    } else {
      console.error(`Chat prompt stream ended as: ${result.outcome}`);
      // Tell the user the answer failed. An empty bubble looks like the reply
      // is still on its way.
      if (this.messages[aiMsgIndex] && !this.messages[aiMsgIndex].text) {
        this.messages[aiMsgIndex].text = 'That answer did not come through. Please send your message again.';
        this.messagesChange.emit(this.messages);
      }
    }

    this.focusChatInput();
  }

  scrollToBottom() {
    setTimeout(() => {
      const el = this.messagesContainer.nativeElement;
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  }


  /**
   * Streams a background prompt to the chat service and returns the JSON the
   * answer carries.
   *
   * Warning: this method drives the global loading overlay, which blocks the
   * whole page. Every exit path must clear the overlay. A path that leaves it
   * on freezes the app (868kuzmap).
   *
   * This method never rejects. On any failure it resolves null, so the caller
   * continues without the chat data instead of stopping.
   */
  async triggerBackgroundOCRChat(systemPrompt: any, returnResponse: boolean = false): Promise< any | null> {
    if (!systemPrompt || !this.chatId) return null;

    this.loadingService.triggerLoadingEvent(true);

    const body = {
      aiAssistantId: this.aiAssistantId,
      chatId: this.chatId,
      prompt: `${JSON.stringify(systemPrompt)}`
    };

    const result = await streamChatOnce({
      url: this.chatApiUrl,
      body,
      timeoutMs: RapQuestionnaireComponent.CHAT_TIMEOUT_MS,
      streamFn: this.streamFn
    });

    // streamChatOnce always settles, so this line always runs. No branch above
    // it may return early, or the overlay stays on and the page freezes.
    this.loadingService.triggerLoadingEvent(false);

    if (result.outcome !== 'closed') {
      console.error(`Background OCR chat ended as: ${result.outcome}`);
      return null;
    }

    return returnResponse ? parseChatJson(result.data) : null;
  }

loadOldMessages(chatId: number) {
  this.taxPlanningService.getChatHistory(this.org,this.user,chatId).subscribe({
    next: (res: any) => {
      if (!res?.data?.generatedMessage) return;
      const historyArray = [...res.data.generatedMessage].reverse().slice(1); ;

      const formattedMessages: ChatMessage[] = [];

      historyArray.forEach((item: any) => {
        
        const prompt = item.userPrompt?.trim()?.replace(/^"|"$/g, '');
        if (prompt?.toLowerCase() === 'end conversation') {
          return;
        }

        if (item.userPrompt && item.promptType === 'User_Prompt') {
          formattedMessages.push({
            sender: 'user',
            text: item.userPrompt
          });
        }

        if (item.aiMessage) {
          formattedMessages.push({
            sender: 'ai',
            text: item.aiMessage,
            showCopy: true,
            copied: false,
            messageId: item.aiMessageId,
            userRating: item.userRating
          });
        }

      });

      this.messages = [...this.messages, ...formattedMessages];
      this.messagesChange.emit(this.messages);

      setTimeout(() => this.scrollToBottom(), 100);

    },
    error: err => {this.loadingService.triggerLoadingEvent(false)},
    complete: () => this.loadingService.triggerLoadingEvent(false)
  });
}

 goToNextSection(section: string) {
    this.nextClicked.emit(section);
  }

  async showAiQuestionnarieSection(){
    trackPendoEvent('Discovery Find More Clicked');
     if (!this.messages || this.messages.length === 0) {
      await this.getWelcomeMessage();
      if(this.chatId){
      this.loadOldMessages(this.chatId);
      }
    }
    if (!this.chatId || Number(this.chatId === 0)) {
      this.initializeChatId();
    } 
    trackPendoEvent('Discovery Chat Viewed');
    this.sectionChange.emit(1); // stay in Chat
    this.updateQuestionnarieStep('AI_CHAT');

  }

  showTopStrategiesView(){
    trackPendoEvent('Discovery Chat Canceled');
    this.sectionChange.emit(0); // stay in Top 5 Stratgies
    this.updateQuestionnarieStep('TOP_5_STRATEGIES');
  }

  async restartQuestionnaire(){
    trackPendoEvent('Discovery Chat Restarted');
    this.chatId = 0;
    this.messages =[];
    localStorage.removeItem(`chatId_${this.plan.id}`);
    await this.getWelcomeMessage();
    
    if (!this.chatId || Number(this.chatId === 0)) {
      this.initializeChatId();
    }
  }

focusChatInput() {
  setTimeout(() => {
    if (this.feedbackIndex !== null) return;

    const active = document.activeElement as HTMLElement;
    if (active?.closest('.feedback-panel')) return;
    if (active?.tagName === 'INPUT' || active?.tagName === 'TEXTAREA') return; // ← catches any input

    const el = this.chatInput?.nativeElement;
    if (el && active !== el) {
      el.focus();
    }
  }, 100);
}

  updateQuestionnarieStep(step: 'AI_CHAT' | 'TOP_5_STRATEGIES') {
  // RAP sandbox: Do NOT call backend - questionnaire_track stays in session only.
  // Normal flow: persist to database.
  if (!this.isRapEditMode) {
    const finalData = { questionnaireTrack: step };
    this.taxPlanningService
      .updateQuestionnarieStep(this.org, this.user, this.documentId, finalData)
      .subscribe(response => {
        console.log('Updated questionnaireTrack:', step);
      });
  }
 }

 feedbackIndex: number | null = null;
 feedbackText = '';

 async onCopyMessage(index: number) {
  const msg = this.messages[index];
  if (!msg?.text) return;

  let textToCopy = this.getMessagePlainText(index);
  if (!textToCopy) {
    textToCopy = this.sanitizeMessageForCopy(msg.text);
  }

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(textToCopy);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }

    this.copiedIndex = index;
    setTimeout(() => {
      if (this.copiedIndex === index) {
        this.copiedIndex = null;
      }
    }, 1500);
  } catch (error) {
    console.error('Failed to copy message:', error);
  }
 }

 copyMessage(index: number) {
  this.onCopyMessage(index);
 }

 private getMessagePlainText(index: number): string {
  const messageRows = document.querySelectorAll('.messages .message-row.ai');
  const messageEl = messageRows[index]?.querySelector('.bubble') as HTMLElement | null;
  if (!messageEl) return '';

  const clonedMessageEl = messageEl.cloneNode(true) as HTMLElement;

  const actionArea = clonedMessageEl.querySelector('.rating-actions');
  const feedbackPanel = clonedMessageEl.querySelector('.feedback-panel');

  if (actionArea) {
    actionArea.remove();
  }
  if (feedbackPanel) {
    feedbackPanel.remove();
  }

  const text = (clonedMessageEl.innerText || clonedMessageEl.textContent || '')
    .replace(/\u00A0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();

  return text;
 }

 private sanitizeMessageForCopy(rawText: string): string {
  return rawText
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/\u00A0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
 }

 rateMessage(index: number, rating: 1 | -1) {
  const msg = this.messages[index];
  if (!msg?.messageId) return;

  const isToggleOff = msg.userRating === rating;
  const newRating = isToggleOff ? null : rating;
  const apiValue = isToggleOff ? 0 : rating;

  this.messages[index] = { ...msg, userRating: newRating };
  this.messagesChange.emit(this.messages);

  if (rating === -1 && !isToggleOff) {
    this.feedbackIndex = index;
    this.feedbackText = '';
    this.knolliChatService.rateMessage(msg.messageId, -1).subscribe();
    if (index === this.messages.length - 1) {
      setTimeout(() => {
        const container = this.messagesContainer?.nativeElement;
        if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }, 50);
    }
  } else {
    if (this.feedbackIndex === index) this.closeFeedback();
    this.knolliChatService.rateMessage(msg.messageId, apiValue as any).subscribe();
  }
}

 closeFeedback() {
  this.feedbackIndex = null;
  this.feedbackText = '';
  this.focusChatInput(); 
}

 submitFeedback(index: number) {
  const msg = this.messages[index];
  if (!msg?.messageId) return;
  this.knolliChatService.rateMessage(msg.messageId, -1, this.feedbackText.trim() || undefined).subscribe();
  this.closeFeedback();

}

}
